<template>
  <div class="tabs-container" :class="containerClass">
    <template v-if="isRouterMode">
      <template v-for="tab in tabs" :key="tab.to">
        <router-link v-if="!tab.disabled" class="tabs-container-tab" :to="tab.to" :data-active="isRouterTabActive(tab)">
          {{ tab.label }}
          <div v-if="tab.indicator" class="tabs-container-indicator">{{ tab.indicator }}</div>
        </router-link>
        <span v-else class="tabs-container-tab tabs-container-tab--disabled">
          {{ tab.label }}
          <div v-if="tab.indicator" class="tabs-container-indicator">{{ tab.indicator }}</div>
        </span>
      </template>
    </template>
    <template v-else>
      <button v-for="tab in tabs" :key="tab.id" class="tabs-container-tab"
        :class="{ active: modelValue === tab.id, 'tabs-container-tab--disabled': tab.disabled }"
        v-tooltip="tab.tooltip || null" @click="!tab.disabled && $emit('update:modelValue', tab.id)">
        <span v-if="tab.dot" class="tabs-container-tab-dot" :style="{ background: tab.dot }"></span>
        <span v-else-if="tab.icon" class="tabs-container-tab-icon-wrap"
          :style="tab.iconBg ? { background: tab.iconBg } : {}">
          <img :src="tab.icon" class="tabs-container-tab-icon" />
        </span>
        {{ tab.label }}
      </button>
    </template>
  </div>
</template>

<script>
export default {
  name: 'TabsContainer',
  props: {
    tabs: {
      type: Array,
      required: true
    },
    modelValue: {
      type: String,
      default: ''
    },
    variant: {
      type: String,
      default: 'panel',
      validator: (value) => ['panel', 'underline'].includes(value)
    }
  },
  computed: {
    isRouterMode() {
      return this.tabs.length > 0 && 'to' in this.tabs[0]
    },
    containerClass() {
      return {
        'tabs-container--panel': this.variant === 'panel',
        'tabs-container--underline': this.variant === 'underline'
      }
    }
  },
  methods: {
    isRouterTabActive(tab) {
      const tabPath = typeof tab.to === 'object' ? tab.to.path : tab.to;
      if (tab.exact) {
        return this.$route.path === tabPath
      }
      // A tab whose view is reachable from more than one URL lists them all
      if (tab.matchPaths) {
        if (!Array.isArray(tab.matchPaths)) throw Error('TabsContainer: tab.matchPaths must be an array of paths')
        return tab.matchPaths.some(path => this.$route.path.startsWith(path))
      }
      return this.$route.path.startsWith(tabPath)
    }
  },
  emits: ['update:modelValue']
}
</script>

<style scoped>
.tabs-container {
  display: flex;
  overflow: hidden;
  flex-shrink: 0;
}

.tabs-container--panel {
  background: #EEE;
  border-radius: 4px;
}

.tabs-container--panel .tabs-container-tab {
  text-decoration: none;
  color: unset;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.625rem;
  background: transparent;
  font-size: 0.875rem;
  font-family: inherit;
  cursor: pointer;
}

.tabs-container--panel .tabs-container-tab[data-active="true"],
.tabs-container--panel .tabs-container-tab.active {
  background: #F9F9F9;
  font-weight: 600;
  box-shadow: 0 0px 8px rgba(0, 0, 0, 0.08);
}

/* A row that grows with its content (one tab per speaker, say) scrolls rather than
   clipping the tabs past the edge. Scrollbar hidden, as elsewhere in the panels. */
.tabs-container--underline {
  gap: 0.25rem;
  border-bottom: 1px solid #e0e0e0;
  overflow-x: auto;
  scrollbar-width: none;
}

.tabs-container--underline::-webkit-scrollbar {
  display: none;
}

.tabs-container--underline .tabs-container-tab {
  flex-shrink: 0;
}

.tabs-container--underline .tabs-container-tab {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 1.25rem;
  border: none;
  background: none;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  font-weight: 500;
  color: #666;
  transition: all 0.2s;
  font-size: 0.875rem;
  font-family: inherit;
}

.tabs-container--underline .tabs-container-tab:hover {
  background-color: #f5f5f5;
}

.tabs-container--underline .tabs-container-tab[data-active="true"],
.tabs-container--underline .tabs-container-tab.active {
  color: var(--color-primary, #6a16f0);
  border-bottom-color: var(--color-primary, #6a16f0);
  font-weight: 600;
}

.tabs-container-tab--disabled {
  opacity: 0.4;
  cursor: default;
}

.tabs-container-tab-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.tabs-container-tab-icon-wrap {
  width: 22px;
  height: 22px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.tabs-container-tab-icon {
  width: 14px;
  height: 14px;
}

.tabs-container-indicator {
  position: relative;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 0.875rem;
  height: 0.875rem;
  font-size: 0.5625rem;
  font-weight: 600;
  background: var(--color-primary, #6a16f0);
  color: #FFF;
  border-radius: 7px;
  margin-left: 0.25rem;
  margin-bottom: 0.0625rem;
  vertical-align: middle;
}
</style>
